import React from "react";
import styles from "../PasswordSetup/PasswordSetup.module.scss";
import RegistrationInput from "../RegistrationInput/RegistrationInput.tsx";
import Button from "../Forms/Button/Button.js";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import PDF from "../../Footer/PDF/Terminos-y-condiciones-edify-2021.pdf";
import { usePasswordSetup } from "../../../hooks/registration/usePasswordSetup.ts";
import { useRegistrationFlow } from "../../../hooks/registration/useRegistrationFlow.ts";

interface PasswordSetupV2Props {
  email: string;
}

const PasswordSetupV2: React.FC<PasswordSetupV2Props> = ({ email }) => {
  const registrationFlow = useRegistrationFlow();
  const {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    passwordError,
    setPasswordError,
    confirmPasswordError,
    setConfirmPasswordError,
    skipOnboarding,
    setSkipOnboarding,
    acceptTerms,
    setAcceptTerms,
    isSubmitting,
    handlePasswordSubmit,
    errorMessage,
  } = usePasswordSetup({
    email,
    completeUserRegistration: registrationFlow.completeUserRegistration,
    completeRegistrationLoading: registrationFlow.completeRegistration.isLoading,
    completeRegistrationError: registrationFlow.completeRegistration.error,
  });

  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState<boolean>(false);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setPassword(value);
    
    if (passwordError) {
      setPasswordError("");
    }
    
    if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError("Las contraseñas no coinciden");
    } else if (confirmPassword && value === confirmPassword) {
      setConfirmPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setConfirmPassword(value);
    
    if (confirmPasswordError) {
      setConfirmPasswordError("");
    }
  };

  const handlePasswordBlur = (): void => {
    if (password && passwordError) {
      setPasswordError("");
    }
  };

  const handleConfirmPasswordBlur = (): void => {
    if (confirmPassword && confirmPasswordError) {
      setConfirmPasswordError("");
    }
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = (): void => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await handlePasswordSubmit();
  };

  const formVariant = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariant = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  return (
    <motion.div
      variants={formVariant}
      initial="hidden"
      animate="visible"
      className={styles.container}
    >
      <motion.div variants={itemVariant} className={styles.content}>
        <motion.p 
          variants={itemVariant}
          className={styles.subtitle}
        >
          Contraseña
        </motion.p>

        <motion.div variants={itemVariant} className={styles.emailDisplay}>
          <span className={styles.emailValue}>{email}</span>
        </motion.div>

        <motion.form variants={itemVariant} className={styles.form} onSubmit={handleSubmit}>
          <RegistrationInput
            type={showPassword ? "text" : "password"}
            placeholder="Ingresa tu contraseña"
            value={password}
            onChange={handlePasswordChange}
            onBlur={handlePasswordBlur}
            error={passwordError}
            disabled={isSubmitting}
            autoComplete="new-password"
            rightIcon={
              showPassword ? (
                <EyeOff size={20} />
              ) : (
                <Eye size={20} />
              )
            }
            onRightIconClick={togglePasswordVisibility}
            className={styles.passwordInput}
          />

          <RegistrationInput
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirma tu contraseña"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            onBlur={handleConfirmPasswordBlur}
            error={confirmPasswordError}
            disabled={isSubmitting}
            autoComplete="new-password"
            rightIcon={
              showConfirmPassword ? (
                <EyeOff size={20} />
              ) : (
                <Eye size={20} />
              )
            }
            onRightIconClick={toggleConfirmPasswordVisibility}
            className={styles.passwordInput}
          />

          {errorMessage && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={styles.errorMessage}
            >
              {errorMessage}
            </motion.div>
          )}

          <motion.div 
            variants={itemVariant}
            className={styles.checkboxesContainer}
          >
            <div className={styles.checkboxContainer}>
              <input 
                type="checkbox" 
                id="acceptTerms"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className={styles.checkbox}
              />
              <label htmlFor="acceptTerms" className={styles.checkboxLabel}>
                <span className={styles.termsText}>
                  Acepto los{" "}
                  <a 
                    href={PDF} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={styles.termsLink}
                  >
                    términos y condiciones
                  </a>
                </span>
              </label>
            </div>

            <div className={styles.checkboxContainer}>
              <input 
                type="checkbox" 
                id="skipOnboarding"
                checked={skipOnboarding}
                onChange={(e) => setSkipOnboarding(e.target.checked)}
                className={styles.checkbox}
              />
              <label htmlFor="skipOnboarding" className={styles.checkboxLabel}>
                <span className={styles.skipText}>
                  Completar mi perfil más tarde
                </span>
                <span className={styles.skipSubtext}>
                  (datos básicos por defecto)
                </span>
              </label>
            </div>
          </motion.div>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !password || !confirmPassword || !!passwordError || !!confirmPasswordError || !acceptTerms}
            className={styles.btn}
            colored
            bordered={false}
            link={false}
            typebtn="primary"
          >
            {isSubmitting ? "Creando cuenta..." : "Crear cuenta"}
          </Button>
        </motion.form>
      </motion.div>
    </motion.div>
  );
};

export default PasswordSetupV2;
